import {
	FieldValues,
	Path,
	addDays,
	addYears,
	calculateAge,
	getTzTodayDate,
	isAfter,
	isBefore,
	mapDatePicker,
	parseIsoDateTime,
} from '@ninja/utilities';
import { useAppPlatformContextOptions } from '../../../../../contexts/app';
import { useSystemConfigurationSettings } from '../../../../app-init-data';
import { IEidMembersForm } from '../../../with-eid-members/members-form/types';
import { IMembersForm } from '../../../without-eid-members';
import { AddFieldError } from '../helpers/add-field-error';
import { addYearsToDate } from '../helpers/add-years-to-date';

export type DateOfBirthFieldType = 'adult' | 'parent' | 'child';

export const useDateOfBirthField = <TForm extends FieldValues>() => {
	const { translate: t } = useAppPlatformContextOptions();
	const { systemConfigurationSettings } = useSystemConfigurationSettings();
	const maxAgeAllowed = systemConfigurationSettings?.maxAgeAllowed ?? 90;

	const getField = ({ field, fieldType }: { field: Path<TForm>; fieldType: DateOfBirthFieldType }) => {
		switch (fieldType) {
			case 'adult':
			case 'parent':
				return mapDatePicker({
					field,
					label: t('members:fields.date-of-birth.label'),
					fromDate: addYears(getTzTodayDate(), -maxAgeAllowed),
					toDate: addYears(getTzTodayDate(), -18),
					defaultMonth: addYears(getTzTodayDate(), -18),
					isDateOfBirth: true,
					format: 'date-only',
				});
			case 'child':
				return mapDatePicker({
					field,
					label: t('members:fields.date-of-birth.label'),
					fromDate: addYears(getTzTodayDate(), -maxAgeAllowed),
					toDate: getTzTodayDate(),
					isDateOfBirth: true,
					format: 'date-only',
				});
		}
	};

	const getValidation = ({
		formValues,
		fieldType,
		addFieldError,
		dateOfBirth,
	}: {
		formValues: IEidMembersForm | IMembersForm;
		fieldType: DateOfBirthFieldType;
		addFieldError: ({ field, message }: AddFieldError) => void;
		dateOfBirth: string;
	}) => {
		const dob = parseIsoDateTime(dateOfBirth) as Date;
		const principleDateOfBirth = parseIsoDateTime(formValues.principalDetails?.dateOfBirth);

		switch (fieldType) {
			case 'child': {
				if (calculateAge(dob) >= 18)
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.child.less-18'),
					});
				else if (isBefore(dob, addYearsToDate(principleDateOfBirth, 15)))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.child.min'),
					});
				else if (isAfter(new Date(dob.toDateString()), new Date(getTzTodayDate().toDateString())))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.child.max'),
					});

				break;
			}

			case 'parent': {
				if (isBefore(dob, addYears(getTzTodayDate(), -maxAgeAllowed)))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.parent.min', { maxAge: maxAgeAllowed }),
					});
				else if (!isBefore(addDays(dob, -1), addYearsToDate(principleDateOfBirth, -15)))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.parent.max'),
					});
				break;
			}
			case 'adult':
			default: {
				if (isBefore(dob, addYears(getTzTodayDate(), -maxAgeAllowed)))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.adult.min', { maxAge: maxAgeAllowed }),
					});
				else if (!isBefore(addDays(dob, -1), addYears(getTzTodayDate(), -18)))
					addFieldError({
						field: 'dateOfBirth',
						message: t('members:fields.date-of-birth.validation.adult.max'),
					});
			}
		}
	};

	return {
		getField,
		getValidation,
	};
};
